// OverlayAdminController — die Orchestrierung des Admin-Overlay-Stacks (Schnitt 5, „Dirigent zuerst").
// Hält die aktive-Overlay-Toolbar, die Lifecycle-/Persistenz-Funktionen UND das gesamte Event-Wiring
// (bind*). overlay.js ist damit reiner Bootstrap: Karte + Seams + Module erzeugen, dann controller.init().
//
// Deps: { map, mapView, mapBackend, $mapCanvas, state, dirty, gateway }. Globals: window.$ und das
// Legacy-`topo` (basic.js, top-level `let` → globale lexikalische Bindung, NICHT window.topo → bare nutzen).
// DOM bleibt vorerst Datenwahrheit (one-way ist die Folgephase). Karten-Zugriffe laufen über mapView/mapBackend
// (google-frei).
//
// Gateway-Schritt b: Save/Delete laufen über den Mediator-Bus (publish PERSIST_REQUEST/DELETE_REQUEST →
// Persister-Subscriber → PERSISTED/DELETED → Response-Behandlung). So können export_json/History/Dev-Persister
// additiv andocken, ohne den Controller zu ändern.

import { mediator } from "../mediator/mediatorInstance.js";
import { OVERLAY_EVENTS } from "./overlayEvents.js";
import { str } from "../i18n/str.js";

export function createOverlayAdminController(deps) {
    const $ = window.$;
    // `topo` (basic.js) ist ein top-level `let` → globale LEXIKALISCHE Bindung, NICHT window.topo.
    // Bare `topo` löst über die von Modulen geteilte globale Umgebung auf; window.topo wäre undefined.
    const map = deps.map;
    const mapView = deps.mapView;
    const mapBackend = deps.mapBackend;
    const $mapCanvas = deps.$mapCanvas;
    const state = deps.state;
    const dirty = deps.dirty;
    const gateway = deps.gateway;

    var toolbar;   // unten erzeugt; die Funktionen referenzieren es erst zur Laufzeit (nach Zuweisung)

    // ── Lifecycle / Persistenz ──────────────────────────────────────────────────────────────────────

    // Platzierungs-Status pro Zeile als Klasse (treibt CSS: Titelfarbe, Auge, „(nicht auf Karte)").
    function markPlaced(id, placed) {
        $('#tr' + id).toggleClass('placed', placed).toggleClass('notplaced', !placed)
            .attr('title', placed ? null : str('OVERLAY_NOT_ON_MAP'));
    }

    function addOverlay(id, src, insSW, insNE, rotation, distort) {
        let bounds = mapView.bounds(insSW, insNE);
        // ctx.id = aktuelle Ref dieser Zeile; rekeyRow aktualisiert sie nach m->o (Closure-Falle vermeiden:
        // die Editor-Callbacks lesen ctx.id, nicht das fixe id).
        let ctx = { id: id };
        let po = mapBackend.createOverlayEditor(map, {
            imageUrl: src,
            bounds: bounds,
            opacity: 90,   // Live-Helfer, nicht persistiert — Steuerung über den Toolbar-Slider
            rotation: rotation || 0,
            distort: distort || null,
            onChange: function (b) {
                update(ctx.id);
                if (ctx.id === state.getActiveId()) { toolbar.draw(); }   // Toolbar folgt Verschieben/Skalieren
            },
            onSelect: function () { activateOverlay(ctx.id); },
            onRotateChange: function (deg) {
                $('#rotation' + ctx.id).val(deg); dirty.recompute(ctx.id);
                if (ctx.id === state.getActiveId()) { toolbar.draw(); }   // Toolbar folgt der Rotation
            },
            onModeChange: function (mode) {
                if (ctx.id === state.getActiveId()) { toolbar.setMode(mode); }
            },
            onHistoryChange: function (canUndo, canRedo, pos, len) {
                if (ctx.id === state.getActiveId()) { toolbar.setHistoryState(canUndo, canRedo, pos, len); }
            },
            // Nach JEDEM Editor-draw die Toolbar nachziehen. Google zeichnet die OverlayViews in unbestimmter
            // Reihenfolge; kam die Toolbar zuerst dran, rechnete sie mit der alten Editor-Geometrie. Während einer
            // Bewegung fiel das nicht auf (nächster Frame korrigierte), am Ende eines Pans blieb genau die
            // Pan-Strecke als Versatz stehen. Hier ist die Reihenfolge erzwungen statt dem Zufall überlassen.
            onAfterDraw: function () {
                if (ctx.id === state.getActiveId()) { toolbar.draw(); }
            }
        });
        state.setEntry(id, { overlay: po, initialBounds: bounds, ctx: ctx });
        markPlaced(id, true);
        $('#editorVisible' + id).prop('checked', true);   // a newly placed overlay is shown on the editor map
    }

    // Schreibt die Editor-Bounds in die Hidden-Inputs + Georef-Boxen; ohne init → Dirty neu rechnen.
    function update(id, init) {
        var entry = state.get(id);
        if (entry) {
            var b = entry.overlay.getBounds();
            var posSw = b.getSouthWest();
            var posNe = b.getNorthEast();
            var geo = {'lat_sw':posSw.lat(),'lng_sw':posSw.lng(),'lat_ne':posNe.lat(),'lng_ne':posNe.lng()};
            $('#inpSW' + id).val(geo.lat_sw + ',' + geo.lng_sw);
            $('#inpNE' + id).val(geo.lat_ne + ',' + geo.lng_ne);
            $('#geoLatSW' + id).val(geo.lat_sw);
            $('#geoLngSW' + id).val(geo.lng_sw);
            $('#geoLatNE' + id).val(geo.lat_ne);
            $('#geoLngNE' + id).val(geo.lng_ne);
        } else {
            $('#inpSW' + id + ',#inpNE' + id).val('');
        }
        if (!init) {
            if (id) { dirty.recompute(id); }
        }
    }

    // Genau ein Overlay aktiv (solide), die anderen blass; Zeile hervorheben + Toolbar ankoppeln.
    function activateOverlay(id) {
        state.setActiveId(id);
        state.forEachEditor(function (editor, k) {
            editor.setActive(k == id);
        });
        $('#overlayDataContainer .overlayRow').removeClass('active');
        var $row = $('#tr' + id).addClass('active');
        if ($row.length && $row[0].scrollIntoView) {
            $row[0].scrollIntoView({block: 'nearest'});
        }
        var ov = state.getEditor(id);
        if (ov && ov.isHidden && ov.isHidden()) {   // selected from the list while hidden → show it again
            ov.setHidden(false);
            $('#editorVisible' + id).prop('checked', true);
        }
        if (ov) {
            toolbar.attachTo(ov);
            toolbar.show();
            if (ov.canUndo) { toolbar.setHistoryState(ov.canUndo(), ov.canRedo(), ov.getHistoryPos(), ov.getHistoryLen()); }
        }
    }

    function deselectOverlays() {
        state.clearActive();
        state.forEachEditor(function (editor) {
            editor.setActive(false);
        });
        $('#overlayDataContainer .overlayRow').removeClass('active');
        toolbar.hide();
    }

    // Speichert das aktuell gewählte Overlay (Toolbar-OK / Strg-Enter).
    function saveActiveOverlay() {
        if (!state.getActiveId()) { return; }
        save(dataForId([state.getActiveId()]));
        $('#save' + state.getActiveId()).buttonLoading(true);
        var ov = state.getActiveEditor();
        if (ov && ov.resetHistory) {
            ov.resetHistory();
        }
    }

    // Auf Ausgangszustand zurück (initialBounds, Rotation 0, Verzerrung weg).
    function resetOverlayToInitial(id) {
        var o = state.get(id);
        if (!o || !o.overlay || !o.initialBounds) { return; }
        o.overlay.setDistort(false);
        o.overlay.change(o.initialBounds);
        o.overlay.setRotation(0);
        update(id);
    }

    // Stapel-Reihenfolge (z-index) aus der Listenreihenfolge (oben = vorne).
    function applyLayerOrder() {
        var $rows = $('#overlayDataContainer').children('.overlayRow');
        var n = $rows.length;
        $rows.each(function (i) {
            var id = this.id.replace(/tr/, '');
            $(this).find('.layerNo').text(i + 1);
            var ov = state.getEditor(id);
            if (ov && ov.setZIndex) {
                ov.setZIndex(n - i);
            }
        });
    }

    // Persistiert die Reihenfolge (leiser Batch-Save, kein Dirty); refs → live rekey.
    function saveLayerOrder() {
        var data = {};
        $('#overlayDataContainer').children('.overlayRow').each(function (i) {
            data[this.id.replace(/tr/, '')] = { sort_order: i };
        });
        gateway.saveOverlays(data).done(function (json) {
            if (json && json.refs) {
                for (var sent in json.refs) { rekeyRow(sent, json.refs[sent]); }
            }
        });
    }

    function saveAll() {	// holt alle ids aus den trs und speichert sie
        save(dataForId((function () {
            var ids = [];
            $('#overlayDataContainer').children('.overlayRow').each(function () {
                ids.push(this.id.replace(/tr/g, ''));
            });
            return ids;
        })()));
    }

    function dataForId(ids) {
        var data = {};
        var rows = $('#overlayDataContainer').children('.overlayRow');   // für sort_order = Zeilenindex
        for (var i = 0; i < ids.length; i++) {
            var id = ids[i];
            var NE = $('#inpNE' + id).val().split(',');
            var SW = $('#inpSW' + id).val().split(',');
            data[id] = {'name': $('#name' + id).val(), 'year': $('#year' + id).val(), 'info_text': $('#infoText' + id).val(), 'lat_northeast': NE[0], 'lng_northeast': NE[1], 'lat_southwest': SW[0], 'lng_southwest': SW[1], 'zoom_min': $('#zoom' + id).val().split('-')[0], 'zoom_max': $('#zoom' + id).val().split('-')[1]};
            data[id].sort_order = rows.index(document.getElementById('tr' + id));
            // Editor-Params (rotation/distorted/distortCorners) mergen — serverseitig persistiert.
            var ov = state.getEditor(id);
            if (ov && ov.getParams) {
                $.extend(data[id], ov.getParams());
            }
        }
        return data;
    }

    // m->o-Migration live: DOM-Attribute (trailing-match), sichtbare ID, State-Eintrag + Dirty-Baseline.
    function rekeyRow(oldRef, newRef) {
        if (!oldRef || !newRef || oldRef === newRef) { return; }
        var $row = $('#tr' + oldRef);
        if (!$row.length) { return; }
        var attrs = ['id', 'name', 'data-id'];
        $row.add($row.find('*')).each(function () {
            var el = this;
            for (var i = 0; i < attrs.length; i++) {
                var v = el.getAttribute(attrs[i]);
                if (v && v.length >= oldRef.length && v.slice(-oldRef.length) === oldRef) {
                    el.setAttribute(attrs[i], v.slice(0, -oldRef.length) + newRef);
                }
            }
        });
        $('#tr' + newRef + ' .overlayId').text('ID ' + newRef);
        state.rekey(oldRef, newRef);
        dirty.rekey(oldRef, newRef);
    }

    // Persist-Absicht publizieren statt direkt zu speichern (Gateway-Schritt b). Der Persister + die
    // Response-Behandlung laufen als Subscriber (wireBus).
    function save(sendData) {
        mediator.publish(OVERLAY_EVENTS.PERSIST_REQUEST, { data: sendData });
    }

    // Response auf einen erfolgreichen Persist (Subscriber von PERSISTED).
    function handlePersisted(json) {
        $('input[type=button],button').buttonLoading(false);
        if (json.success) {
            for (var id in json.success) {
                topo.log(dataForId([id]));
                $('#tr' + id + ' > *').highlight(json.success[id]);   // Divs: Zellen (> *) statt <td>
                dirty.markClean(id);
            }
            // nach erfolgreichem Save die m->o-Migration live umschlüsseln (ohne Reload kanonische 'o<id>'-Ref).
            if (json.refs) {
                for (var sent in json.refs) { rekeyRow(sent, json.refs[sent]); }
            }
            // gespeicherten Stand als neue Baseline (nach evtl. rekey die kanonische id) + Dirty aufräumen.
            for (var id in json.success) {
                var curId = (json.refs && json.refs[id]) ? json.refs[id] : id;
                dirty.captureBaseline(curId);
                dirty.recompute(curId);
            }
        }
    }

    // Response auf einen Delete (Subscriber von DELETED).
    function handleDeleted(json, id) {
        if (json.success) {
            $('#tr' + id).fadeOut();
            remove(id);
        } else {
            alert('fehler beim löschen: ' + json.error);
        }
    }

    // Save/Delete über den Bus: der MySQL-Persister ist EIN Subscriber (im Dev gegen NoSQL tauschbar, sobald
    // unsubscribe da ist — kommt mit dem Tagging-Merge); export_json/History docken additiv an :request/:persisted an.
    function wireBus() {
        mediator.subscribe(OVERLAY_EVENTS.PERSIST_REQUEST, function (payload) {
            gateway.saveOverlays(payload.data).done(function (json) {
                mediator.publish(OVERLAY_EVENTS.PERSISTED, { json: json });
            });
        });
        mediator.subscribe(OVERLAY_EVENTS.PERSISTED, function (payload) { handlePersisted(payload.json); });
        mediator.subscribe(OVERLAY_EVENTS.DELETE_REQUEST, function (payload) {
            gateway.deleteOverlay(payload.id).done(function (json) {
                mediator.publish(OVERLAY_EVENTS.DELETED, { json: json, id: payload.id });
            });
        });
        mediator.subscribe(OVERLAY_EVENTS.DELETED, function (payload) { handleDeleted(payload.json, payload.id); });
    }

    function remove(id) {
        var entry = state.get(id);
        if (entry) {
            entry.overlay.remove();
            $('#inpSW' + id + ',#inpNE' + id).val('');
            // Georef-Anzeige leeren — Overlay ist nicht mehr auf der Karte.
            $('#geoLatSW' + id + ',#geoLngSW' + id + ',#geoLatNE' + id + ',#geoLngNE' + id).val('');
            state.setEntry(id, null);
        }
        markPlaced(id, false);
        if (id === state.getActiveId()) {
            toolbar.attachTo(null);
            toolbar.hide();
            state.clearActive();
        }
        update(id);   // MIT id → dirty.recompute(id) läuft (Overlay von der Karte entfernt = dirty)
    }

    // ── Event-Wiring (eine Funktion je Gruppe, von init() aufgerufen) ────────────────────────────────

    // sort: Zeilen per Griff umsortieren → Stapelreihenfolge (z-index).
    function bindSort() {
        $('#overlayDataContainer').sortable({
            handle: '.sortHandle',
            items: '> .overlayRow',
            axis: 'y',
            cancel: 'input,button,textarea,select,a,.slider-vertical,.overlayImage',
            start: function (ev, ui) { ui.item.find('.layerNo').text('?'); },
            stop: function () { applyLayerOrder(); saveLayerOrder(); }   // Ebenen + z-index neu, dann persistieren
        });
    }

    // drop: Vorschaubild auf die Karte ziehen → platzieren.
    function bindDrop() {
        // Drag-Helper bewusst KLEIN (Thumb-Größe) — entkoppelt vom großen Panel-Vorschaubild
        // (.overlayImage width:100%, Chef-Charge 8). Sonst ist der Ghost riesig UND die Drop-Bounds
        // (aus w/h) landen nur weit links-oben noch im Map-Container. Placement nutzt die Helper-Größe.
        var DRAG_THUMB_WIDTH = 200;
        $('.overlayImage').draggable({
            helper: function () {
                return $(this).clone().css({ width: DRAG_THUMB_WIDTH + 'px', height: 'auto', maxWidth: 'none' });
            },
            appendTo: '#main', opacity: .8, scroll: false, zIndex: 2
        });
        if (!$mapCanvas) { return; }
        $mapCanvas.droppable({
            drop: function (ev, ui) {
                var src = ui.draggable[0].src;
                var iid = (ui.draggable[0].id || '').replace(/image/, '');
                if (!src || !iid) { return; }
                // Größe aus dem KLEINEN Helper, NICHT aus dem großen Original-Vorschaubild.
                var w = ui.helper.outerWidth();
                var h = ui.helper.outerHeight();
                var dropSW = mapView.containerPointToLatLng(ui.position.left - 20, ui.position.top + h - 40);
                var dropNE = mapView.containerPointToLatLng(ui.position.left + w - 20, ui.position.top - 40);
                if (!dropSW || !dropNE) {
                    // Karten-Projektion noch nicht initialisiert → kein Crash, erneut ziehen.
                    topo.log('Overlay: Karten-Projektion noch nicht bereit, bitte erneut ziehen.');
                    return;
                }
                var bounds = mapView.bounds(dropSW, dropNE);
                var entry = state.get(iid);
                if (entry) {
                    // Re-Drop = DEFAULT-Objekt: Bounds neu + Skew zurück (change → _initCornersFromBounds
                    // setzt _distorted=false) + Rotation zurück auf 0. Kein rotiertes/verzerrtes Objekt beim Ablegen.
                    entry.overlay.change(bounds);
                    if (entry.overlay.setRotation) { entry.overlay.setRotation(0); }
                    entry.initialBounds = bounds;   // erneutes Ziehen → neuer Ausgangszustand
                } else {
                    addOverlay(iid, src, dropSW, dropNE);
                }
                $('#rotation' + iid).val(0);        // Default-Objekt: Rotation-/Skew-Persistenz leeren (auch fürs Save)
                $('#inpDistort' + iid).val('');
                activateOverlay(iid);   // frisch platziertes Overlay ist aktiv (solide)
                update(iid);
                applyLayerOrder();      // neues Overlay in die Stapel-Reihenfolge einreihen
                mapView.panToBounds(bounds);
            }
        });
    }

    // selection: Klick auf leere Karte hebt auf; Klick auf eine Listenzeile aktiviert + zoomt.
    function bindSelection() {
        mapView.onClick(deselectOverlays);
        $('#overlayDataContainer').on('click', '.overlayRow', function (ev) {
            if ($(ev.target).closest('input, button, a, label, .slider-vertical, .sortHandle').length) {
                return;
            }
            var id = this.id.replace(/tr/, '');
            var ov = state.getEditor(id);
            if (!ov) { return; }
            activateOverlay(id);
            mapView.fitBounds(ov.getBounds(), 80);   // etwas mehr Padding → geringer hineinzoomen
            // Toolbar nach dem Auto-Zoom sicher zeichnen: ein Redraw NACH dem idle hat den Anker → erscheint sofort.
            mapView.onceIdle(function () { toolbar.draw(); });
        });
        // Map-Daten-Spalte (Georef/Rotation) GLOBAL ein-/ausklappen — eine Klasse am Container schaltet alle Zeilen.
        $('#overlayDataContainer').on('click', '.mapDataToggle', function () {
            const open = $('#overlayDataContainer').toggleClass('mapdata-open').hasClass('mapdata-open');
            $('#overlayDataContainer .mapDataToggle').attr('aria-expanded', open ? 'true' : 'false');
        });
    }

    // lifecycle: Overlay endgültig löschen (mit Bestätigungsdialog).
    function bindLifecycle() {
        $('.delete').on('click', function () {
            var id = this.id.replace(/delete/g, '');
            let buttons = [];
            buttons[topo.str('YES')] = function () {
                mediator.publish(OVERLAY_EVENTS.DELETE_REQUEST, { id: id });
                $(this).dialog("close");
            };
            buttons[topo.str('NO')] = function () {
                $(this).dialog("close");
            };
            var tplObj = { 'name': $('#name' + id).val() };
            topo.dialog(topo.str('DELETE_MAP_TITLE'), topo.tpl(topo.str('DELETE_MAP_TEXT'), tplObj), buttons);
        });
        // Remove from the map from the action row (same as the toolbar's exit button, but for any row): the row becomes
        // dirty, OK saves it. data-id read at click time — a legacy row can be rekeyed (m->o) after a save.
        $('#overlayDataContainer').on('click', '.removeFromMapAction', function () {
            remove($(this).closest('.overlayRow').attr('data-id'));
        });
    }

    // persistence: einzeln speichern / alle speichern / Strg-Enter.
    function bindPersistence() {
        $('.save').on('click', function (ev) {
            save(dataForId(new Array(this.id.replace(/save/g, ''))));
            ev.preventDefault();
            $(this).buttonLoading(true);
        });
        $('.saveall').on('click', function (ev) {
            saveAll();
            ev.preventDefault();
            $(this).buttonLoading(true);
        });
        // Strg+Enter: in einem Zeilen-Feld → Wert committen (Georef/Rotation übernehmen), dann DIESE Zeile speichern
        // (wie ihr Disketten-Knopf). Außerhalb der Felder (z. B. grafischer Editor) → aktives Overlay speichern.
        $(document).on('keydown', function (ev) {
            if (!ev.ctrlKey || ev.keyCode !== 13) { return; }
            var $row = $(ev.target).closest('.overlayRow');
            if ($row.length) {
                var id = $row.attr('data-id');
                $(ev.target).trigger('change');   // Wert committen, bevor save liest (sonst alte Geometrie/Rotation)
                ev.preventDefault();
                save(dataForId([id]));
                $('#save' + id).buttonLoading(true);
            } else if (state.getActiveId()) {
                ev.preventDefault();
                saveActiveOverlay();
            }
        });
    }

    // publish: "shown in the frontend" — a toggle button in the action row, saves right away; a failed save puts it back.
    // (Was the eye checkbox; the eye now only shows/hides the overlay on the editor map.)
    function bindVisibility() {
        $('#overlayDataContainer').on('click', '.publishAction', function () {
            var $btn = $(this);
            var id = $btn.closest('.overlayRow').attr('data-id');
            var published = $btn.attr('aria-pressed') !== 'true';
            var data = {};
            data[id] = {visible: published ? 1 : 0};
            $btn.attr('aria-pressed', published ? 'true' : 'false');
            gateway.saveOverlays(data).done(function (json) {
                // Toggling can migrate a legacy row (m->o) → rekey the row live.
                if (json && json.refs) {
                    for (var sent in json.refs) { rekeyRow(sent, json.refs[sent]); }
                }
            }).fail(function () {
                $btn.attr('aria-pressed', published ? 'false' : 'true');
            });
        });
    }

    // editor eye: show/hide the overlay on the editor map only — nothing is saved, a reload shows all again. Hiding the
    // selected overlay deselects it first (its toolbar would float over nothing).
    function bindEditorVisibility() {
        $('#overlayDataContainer').on('change', '.editorVisibleToggle', function () {
            var id = $(this).closest('.overlayRow').attr('data-id');
            var ov = state.getEditor(id);
            if (!ov || !ov.setHidden) { return; }
            if (!this.checked && id === state.getActiveId()) { deselectOverlays(); }
            ov.setHidden(!this.checked);
        });
    }

    // transform: Rotation per Texteingabe (0 = zurücksetzen).
    function bindTransform() {
        $('.rotationInput').on('change', function () {
            var id = this.id.replace(/rotation/, '');
            var ov = state.getEditor(id);
            if (ov && ov.setRotation) {
                ov.setRotation(parseFloat(this.value) || 0);
                dirty.recompute(id);
            }
        });
    }

    // docInfo: Felder mit .marksDirty (Name/Jahr/Zoom …) → Zeile „nicht gespeichert" (Row-ID via data-id).
    function bindDocInfo() {
        $('#overlayDataContainer').on('input', '.marksDirty', function () {
            dirty.recompute($(this).closest('.overlayRow').attr('data-id'));
        });
    }

    // mapProps: Georeferenz-Eingabe (Neupositionierung) + Tastatur-Nudge.
    function bindMapProps() {
        // Georeferenz editierbar: exakte SW/NO eintippen → Overlay neu positionieren (achsenparallele Box).
        // Tolerant gegen vertauschte Ecken (min/max); ungültige Eingabe → auf aktuelle Werte zurück.
        $('.georefLat, .georefLng').on('change', function () {
            var id = this.id.replace(/^geo(Lat|Lng)(SW|NE)/, '');
            var ov = state.getEditor(id);
            if (!ov) { return; }
            var latSW = parseFloat($('#geoLatSW' + id).val());
            var lngSW = parseFloat($('#geoLngSW' + id).val());
            var latNE = parseFloat($('#geoLatNE' + id).val());
            var lngNE = parseFloat($('#geoLngNE' + id).val());
            if ([latSW, lngSW, latNE, lngNE].some(isNaN)) {
                update(id);   // ungültige Eingabe verwerfen
                return;
            }
            var sw = mapView.latLng(Math.min(latSW, latNE), Math.min(lngSW, lngNE));
            var ne = mapView.latLng(Math.max(latSW, latNE), Math.max(lngSW, lngNE));
            ov.change(mapView.bounds(sw, ne));
            update(id);   // Hidden-Inputs + Boxen normalisieren
        });

        // Tastatur-Nudge: Pfeiltasten verschieben das aktive Overlay (Shift = größere Schritte). Nicht in Feldern.
        $(document).on('keydown', function (ev) {
            var ov = state.getActiveEditor();
            if (!ov) { return; }
            var t = ev.target;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) { return; }
            var step = ev.shiftKey ? 10 : 1;
            var dx = 0, dy = 0;
            switch (ev.keyCode) {
                case 37: dx = -step; break;   // ←
                case 38: dy = -step; break;   // ↑
                case 39: dx = step; break;    // →
                case 40: dy = step; break;    // ↓
                default: return;
            }
            ev.preventDefault();
            ov.nudge(dx, dy);
        });
    }

    // ── Aufbau ──────────────────────────────────────────────────────────────────────────────────────

    // Aktive-Overlay-Toolbar: an die Auswahl gekoppelt (attach/show in activateOverlay, hide in deselect/remove).
    toolbar = mapBackend.createOverlayToolbar(map, {
        onMode: function (mode) {
            var ov = state.getActiveEditor();
            if (ov) { ov.setMode(mode); }
        },
        onSave: saveActiveOverlay,
        onReset: function () { if (state.getActiveId()) { resetOverlayToInitial(state.getActiveId()); } },
        onUndo: function () { var ov = state.getActiveEditor(); if (ov) { ov.undo(); } },
        onRedo: function () { var ov = state.getActiveEditor(); if (ov) { ov.redo(); } },
        onClearDistort: function () { var ov = state.getActiveEditor(); if (ov) { ov.clearDistortion(); } },
        onRemoveFromMap: function () { if (state.getActiveId()) { remove(state.getActiveId()); } },
        onOpacity: function (pct) { var ov = state.getActiveEditor(); if (ov) { ov.setOpacity(pct); } }
    });

    // init(): vorhandene Zeilen → Editoren laden, Stapel-Reihenfolge + Dirty-Baseline setzen, Events verdrahten.
    // Voraussetzung: die .overlayRow-Zeilen sind schon gerendert (overlay.js: renderOverlayRows VOR init()).
    function init() {
        wireBus();   // Save/Delete-Bus-Subscriber registrieren (Gateway-Schritt b)
        $('#overlayDataContainer').find('.overlayRow').each(function () {
            let id = this.id.replace(/tr/, '');
            let sw = $('#inpSW' + id).val().split(',');
            let ne = $('#inpNE' + id).val().split(',');
            if (sw.length == 2 && ne.length == 2) {
                var distort = null;
                var distortRaw = $('#inpDistort' + id).val();
                if (distortRaw) { try { distort = JSON.parse(distortRaw); } catch (e) { distort = null; } }
                addOverlay(id, $('#image' + id).attr('src'), mapView.latLng(sw[0], sw[1]), mapView.latLng(ne[0], ne[1]), parseFloat($('#rotation' + id).val()) || 0, distort);
            }
            update(id, true);
        });
        applyLayerOrder();              // initiale Stapel-Reihenfolge aus der Listenreihenfolge
        dirty.captureAllBaselines();    // Baseline nach Render + Editor-Init (post-init DOM = gespeicherter Stand)

        bindSort();
        bindDrop();
        bindSelection();
        bindLifecycle();
        bindPersistence();
        bindVisibility();
        bindEditorVisibility();
        bindTransform();
        bindDocInfo();
        bindMapProps();
    }

    return { init: init };
}
